import type { EGender, IEmployeeDTO } from '@sharedInterfaces/IEmployee';
import type { INameGenderData } from '@sharedInterfaces/IEmployeeTalkState';
import type { EmployeeTalkEventDispatcher } from '@src/App/Services/EmployeeTalkEventDispatcher';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback } from 'react';
import { normalizeString, waitForStateChange } from './utils';

interface PersonalDataDeps {
    dispatcherRef: React.MutableRefObject<EmployeeTalkEventDispatcher | null>;
    isDispatcherReady: () => boolean;
    getComponentData: <T>(id: string) => T | undefined;
    updateComponentData: (id: string, data: unknown) => Promise<void>;
    setSaving: Dispatch<SetStateAction<boolean>>;
    setError: (msg: string | null) => void;
}

export const useEmployeeTalkPersonalData = ({
    dispatcherRef,
    isDispatcherReady,
    getComponentData,
    updateComponentData,
    setSaving,
    setError,
}: PersonalDataDeps) => {
    const updatePersonalData = useCallback(
        async (data: {
            firstName?: string;
            lastName?: string;
            gender?: EGender;
            phoneNumber?: string;
        }): Promise<void> => {
            if (!isDispatcherReady()) return;
            const dispatcher = dispatcherRef.current;
            if (!dispatcher) return;
            try {
                await dispatcher.updatePersonalData(data);
            } catch (err) {
                console.error('❌ Personal Data Update Error:', err);
                setError(err instanceof Error ? err.message : 'Unknown error');
            }
        },
        [dispatcherRef, isDispatcherReady, setError],
    );

    const updateDescription = useCallback(
        async (description: string): Promise<void> => {
            if (!isDispatcherReady()) return;
            const dispatcher = dispatcherRef.current;
            if (!dispatcher) return;
            try {
                await dispatcher.updateDescription(description);
            } catch (err) {
                console.error('❌ Description Update Error:', err);
                setError(err instanceof Error ? err.message : 'Unknown error');
            }
        },
        [dispatcherRef, isDispatcherReady, setError],
    );

    const updatePersonalDataWithSaving = useCallback(
        async (
            data: {
                firstName?: string;
                lastName?: string;
                gender?: EGender;
                phoneNumber?: string;
            },
            componentId: string,
            _currentEmployee: IEmployeeDTO,
        ): Promise<void> => {
            setSaving(true);
            const startTime = Date.now();
            const minDuration = 600;

            try {
                const componentData = getComponentData<INameGenderData>(componentId);
                const updatedComponentData = {
                    ...componentData,
                    firstName: data.firstName ?? componentData?.firstName ?? '',
                    lastName: data.lastName ?? componentData?.lastName ?? '',
                    gender: data.gender ?? componentData?.gender,
                    phoneNumber: data.phoneNumber ?? componentData?.phoneNumber ?? '',
                };

                await updateComponentData(componentId, updatedComponentData);
                await updatePersonalData(data);

                const stateChangePromise = waitForStateChange(() => {
                    const updatedData = getComponentData<INameGenderData>(componentId);
                    if (!updatedData) return false;

                    return (
                        normalizeString(updatedData.firstName) ===
                            normalizeString(data.firstName ?? updatedData.firstName) &&
                        normalizeString(updatedData.lastName) ===
                            normalizeString(data.lastName ?? updatedData.lastName) &&
                        updatedData.gender === (data.gender ?? updatedData.gender) &&
                        normalizeString(updatedData.phoneNumber) ===
                            normalizeString(data.phoneNumber ?? updatedData.phoneNumber)
                    );
                });

                const minDurationPromise = new Promise<void>((resolve) => {
                    const elapsed = Date.now() - startTime;
                    const remaining = Math.max(0, minDuration - elapsed);
                    setTimeout(resolve, remaining);
                });

                await Promise.all([stateChangePromise, minDurationPromise]);
            } finally {
                setSaving(false);
            }
        },
        [getComponentData, updateComponentData, updatePersonalData, setSaving],
    );

    const updateDescriptionWithSaving = useCallback(
        async (description: string, componentId: string, _currentEmployee: IEmployeeDTO): Promise<void> => {
            setSaving(true);
            const startTime = Date.now();
            const minDuration = 600;

            try {
                const componentData = getComponentData<INameGenderData>(componentId);
                const updatedComponentData = {
                    ...componentData,
                    firstName: componentData?.firstName ?? '',
                    lastName: componentData?.lastName ?? '',
                    gender: componentData?.gender,
                    phoneNumber: componentData?.phoneNumber ?? '',
                    description,
                };
                await updateComponentData(componentId, updatedComponentData);
                await updateDescription(description);

                const minDurationPromise = new Promise<void>((resolve) => {
                    const elapsed = Date.now() - startTime;
                    const remaining = Math.max(0, minDuration - elapsed);
                    setTimeout(resolve, remaining);
                });

                await minDurationPromise;
            } finally {
                setSaving(false);
            }
        },
        [getComponentData, updateComponentData, updateDescription, setSaving],
    );

    return {
        updatePersonalData,
        updateDescription,
        updatePersonalDataWithSaving,
        updateDescriptionWithSaving,
    };
};
